import React, { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import axios from "axios";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || `http://${window.location.hostname}:5000`;

const ChatIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);
const TicketIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);
const SendIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const AdminSupportInbox = ({ user }) => {
  const [inboxTab, setInboxTab] = useState("livechat");

  // Live Chat
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const socketRef = useRef(null);
  const chatEndRef = useRef(null);

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketFilter, setTicketFilter] = useState("all");

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const connectAdminSocket = useCallback(() => {
    if (socketRef.current) return;
    const socket = io(SOCKET_URL);
    socketRef.current = socket;
    socket.emit("admin_join");

    socket.on("all_sessions", (existing) => {
      setSessions(existing.map((s) => ({ ...s, unread: 0 })));
    });

    socket.on("new_chat_session", ({ sessionId, userName, messages }) => {
      setSessions((prev) => {
        if (prev.find((s) => s.sessionId === sessionId)) return prev;
        return [{ sessionId, userName, messages, unread: 1 }, ...prev];
      });
    });

    socket.on("session_updated", ({ sessionId, message, userName }) => {
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === sessionId
            ? { ...s, messages: [...(s.messages || []), message], unread: (s.unread || 0) + 1, userName }
            : s
        )
      );
      setActiveSession((curr) => {
        if (curr === sessionId) {
          setChatMessages((prevMsgs) => {
            const isDuplicate = prevMsgs.some(
              (m) => m.text === message.text && m.sender === message.sender &&
                (m.time === new Date(message.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) || m.time === message.time)
            );
            if (isDuplicate) return prevMsgs;
            return [...prevMsgs, { ...message, time: new Date(message.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }];
          });
        }
        return curr;
      });
    });

    socket.on("user_disconnected", ({ sessionId }) => {
      setSessions((prev) => prev.map((s) => (s.sessionId === sessionId ? { ...s, disconnected: true } : s)));
    });

    socket.on("chat_history", ({ sessionId, messages }) => {
      if (sessionId) {
        setChatMessages(messages.map((m) => ({ ...m, time: new Date(m.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })));
      }
    });
  }, []);

  useEffect(() => {
    connectAdminSocket();
    return () => { if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; } };
  }, [connectAdminSocket]);

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/contacts", { headers: { Authorization: `Bearer ${user.token}` } });
      if (data.success) setTickets(data.data);
    } catch (err) { console.error(err); }
    finally { setTicketsLoading(false); }
  }, [user.token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleSelectSession = (sessionId) => {
    setActiveSession(sessionId);
    setChatMessages([]);
    setSessions((prev) => prev.map((s) => (s.sessionId === sessionId ? { ...s, unread: 0 } : s)));
    if (socketRef.current) socketRef.current.emit("admin_join_session", { sessionId });
  };

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !activeSession) return;
    const msgObj = { text: chatInput.trim(), sender: "admin", time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
    setChatMessages((prev) => [...prev, msgObj]);
    setChatInput("");
    if (socketRef.current) socketRef.current.emit("send_message", { sessionId: activeSession, message: msgObj.text, sender: "admin" });
  };

  const handleMarkTicketRead = async (id) => {
    try {
      await axios.put(`/api/contacts/${id}`, {}, { headers: { Authorization: `Bearer ${user.token}` } });
      setTickets((prev) => prev.map((t) => t._id === id ? { ...t, isRead: true } : t));
      if (activeTicket?._id === id) setActiveTicket((t) => ({ ...t, isRead: true }));
    } catch (err) { console.error(err); }
  };

  const handleSelectTicket = (ticket) => {
    setActiveTicket(ticket);
    setTicketReply("");
    if (!ticket.isRead) handleMarkTicketRead(ticket._id);
  };

  const totalUnreadChat = sessions.reduce((acc, s) => acc + (s.unread || 0), 0);
  const totalUnreadTickets = tickets.filter((t) => !t.isRead).length;
  const filteredTickets = tickets.filter((t) => {
    if (ticketFilter === "unread") return !t.isRead;
    if (ticketFilter === "read") return t.isRead;
    return true;
  });

  return (
    <div className="space-y-0">
      {/* Tab Bar */}
      <div className="flex border-b border-[#e1ebf0] bg-white rounded-t-2xl overflow-hidden">
        {[
          { id: "livechat", label: "Live Chat", icon: <ChatIcon />, badge: totalUnreadChat, color: "text-[#46735c] border-[#46735c] bg-[#eaf1ed]/60" },
          { id: "tickets",  label: "Tickets",   icon: <TicketIcon />, badge: totalUnreadTickets, color: "text-[#6992a5] border-[#6992a5] bg-blue-50/60" },
        ].map(({ id, label, icon, badge, color }) => (
          <button
            key={id}
            onClick={() => setInboxTab(id)}
            className={`flex items-center gap-2 px-6 py-3.5 text-sm font-bold transition-all ${
              inboxTab === id ? `${color} border-b-2` : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            {icon}
            {label}
            {badge > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* LIVE CHAT */}
      {inboxTab === "livechat" && (
        <div className="flex h-[540px] rounded-b-2xl border border-t-0 border-[#e1ebf0] overflow-hidden bg-white">
          <div className="w-64 shrink-0 border-r border-[#e1ebf0] bg-[#fbf9f6] flex flex-col">
            <div className="p-4 border-b border-[#e1ebf0]">
              <h3 className="text-sm font-black text-[#46735c] flex items-center gap-2">
                ?? Live Sessions
                {totalUnreadChat > 0 && <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{totalUnreadChat}</span>}
              </h3>
              <p className="text-[10px] text-[#8a7658] font-bold mt-0.5">{sessions.length} active session{sessions.length !== 1 ? "s" : ""}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 p-4 text-center">
                  <span className="text-3xl">??</span>
                  <p className="text-xs text-[#8a7658] font-bold">No active live chats yet.</p>
                </div>
              ) : sessions.map((session) => (
                <button
                  key={session.sessionId}
                  onClick={() => handleSelectSession(session.sessionId)}
                  className={`w-full text-left p-4 border-b border-[#e1ebf0] transition-colors hover:bg-[#eaf1ed] ${activeSession === session.sessionId ? "bg-[#eaf1ed] border-l-4 border-l-[#46735c]" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${session.disconnected ? "bg-gray-400" : "bg-green-500 animate-pulse"}`} />
                      <span className="text-sm font-bold text-gray-800 truncate max-w-[110px]">{session.userName}</span>
                    </div>
                    {session.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">{session.unread}</span>}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 font-bold truncate">
                    {session.disconnected ? "? User disconnected" : session.messages?.slice(-1)[0]?.text || "Started a chat..."}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-white">
            {!activeSession ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
                <span className="text-5xl">??</span>
                <h4 className="text-lg font-black text-[#46735c]">Select a Session</h4>
                <p className="text-sm text-[#8a7658]">Choose a live chat session from the left panel to start replying.</p>
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-[#e1ebf0] bg-[#f8fafb] flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#eaf1ed] rounded-full flex items-center justify-center text-sm">??</div>
                  <div>
                    <p className="text-sm font-black text-gray-800">{sessions.find((s) => s.sessionId === activeSession)?.userName || "Customer"}</p>
                    <p className="text-[10px] text-[#46735c] font-bold">{sessions.find((s) => s.sessionId === activeSession)?.disconnected ? "? Disconnected" : "? Online"}</p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 bg-gray-50">
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full opacity-50">
                      <span className="text-2xl mb-2">??</span>
                      <p className="text-xs font-bold">No messages yet in this session.</p>
                    </div>
                  ) : chatMessages.map((msg, i) => (
                    <div key={i} className={`flex flex-col max-w-[75%] ${msg.sender === "admin" ? "self-end items-end" : "self-start items-start"}`}>
                      <span className="text-[9px] text-gray-400 font-bold mb-0.5 px-1">
                        {msg.sender === "admin" ? "You (Admin)" : sessions.find((s) => s.sessionId === activeSession)?.userName || "Customer"}
                      </span>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm ${msg.sender === "admin" ? "bg-[#6992a5] text-white rounded-tr-sm" : "bg-white text-gray-800 border border-gray-200 rounded-tl-sm"}`}>
                        {msg.text}
                      </div>
                      <span className="text-[9px] text-gray-400 mt-1 px-1">{msg.time}</span>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 bg-white border-t border-[#e1ebf0]">
                  {sessions.find((s) => s.sessionId === activeSession)?.disconnected ? (
                    <p className="text-xs text-center text-gray-400 font-bold py-2">? This user has disconnected.</p>
                  ) : (
                    <form onSubmit={handleSendChat} className="flex gap-2">
                      <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Type your reply..." className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#6992a5] transition-colors" />
                      <button type="submit" disabled={!chatInput.trim()} className="bg-[#6992a5] hover:bg-[#5b7b88] disabled:bg-gray-300 text-white w-10 h-10 rounded-xl flex items-center justify-center transition-colors">
                        <SendIcon />
                      </button>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* TICKETS */}
      {inboxTab === "tickets" && (
        <div className="flex h-[540px] rounded-b-2xl border border-t-0 border-[#e1ebf0] overflow-hidden bg-white">
          <div className="w-72 shrink-0 border-r border-[#e1ebf0] bg-[#fbf9f6] flex flex-col">
            <div className="p-4 border-b border-[#e1ebf0] space-y-2">
              <h3 className="text-sm font-black text-[#6992a5] flex items-center gap-2">
                ?? Contact Tickets
                {totalUnreadTickets > 0 && <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{totalUnreadTickets}</span>}
              </h3>
              <div className="flex gap-1.5">
                {[["all","All"],["unread","Unread"],["read","Read"]].map(([val, label]) => (
                  <button key={val} onClick={() => setTicketFilter(val)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${ticketFilter === val ? "bg-[#6992a5] text-white" : "bg-[#e1ebf0] text-gray-600 hover:bg-[#c9dce7]"}`}>
                    {label}{val === "unread" && totalUnreadTickets > 0 && ` (${totalUnreadTickets})`}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {ticketsLoading ? (
                <div className="flex items-center justify-center h-full"><p className="text-xs text-gray-400 font-bold animate-pulse">Loading tickets...</p></div>
              ) : filteredTickets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 p-4 text-center">
                  <span className="text-3xl">??</span>
                  <p className="text-xs text-[#8a7658] font-bold">No tickets here.</p>
                </div>
              ) : filteredTickets.map((ticket) => (
                <button key={ticket._id} onClick={() => handleSelectTicket(ticket)}
                  className={`w-full text-left p-4 border-b border-[#e1ebf0] transition-colors hover:bg-blue-50/60 ${activeTicket?._id === ticket._id ? "bg-blue-50 border-l-4 border-l-[#6992a5]" : ""}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-gray-800 truncate max-w-[140px]">{ticket.name}</span>
                    {!ticket.isRead && <span className="w-2 h-2 bg-red-500 rounded-full shrink-0" />}
                  </div>
                  <p className="text-[10px] text-[#6992a5] font-bold truncate">{ticket.email}</p>
                  <p className="text-[10px] text-gray-400 mt-1 truncate">{ticket.message}</p>
                  <p className="text-[9px] text-gray-300 font-bold mt-1">{new Date(ticket.createdAt).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col bg-white">
            {!activeTicket ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
                <span className="text-5xl">??</span>
                <h4 className="text-lg font-black text-[#6992a5]">Select a Ticket</h4>
                <p className="text-sm text-gray-400">Click a contact inquiry from the left to view it.</p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-[#e1ebf0] bg-gradient-to-r from-[#6992a5]/10 to-blue-50 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#6992a5] flex items-center justify-center text-white font-black">
                      {activeTicket.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-black text-gray-800">{activeTicket.name}</p>
                      <a href={`mailto:${activeTicket.email}`} className="text-xs text-[#6992a5] hover:underline font-bold">{activeTicket.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${activeTicket.isRead ? "bg-gray-100 text-gray-500" : "bg-red-50 text-red-600 border border-red-200"}`}>
                      {activeTicket.isRead ? "Read" : "? New"}
                    </span>
                    {!activeTicket.isRead && (
                      <button onClick={() => handleMarkTicketRead(activeTicket._id)}
                        className="px-3 py-1.5 bg-[#eaf1ed] hover:bg-[#d5e5db] text-[#46735c] rounded-xl text-[10px] font-bold transition-colors">
                        Mark as Read
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 bg-gray-50 space-y-4">
                  <div className="bg-white rounded-2xl border border-[#e1ebf0] p-5 shadow-sm">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Message</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{activeTicket.message}</p>
                    <p className="text-[10px] text-gray-300 font-bold mt-4 pt-3 border-t border-gray-100">
                      Received: {new Date(activeTicket.createdAt).toLocaleString("en-PH")}
                    </p>
                  </div>

                  <div className="p-4 bg-[#eaf1ed] rounded-2xl border border-[#d5e5db]">
                    <p className="text-xs font-bold text-[#46735c] mb-2">?? Quick Reply Templates</p>
                    <div className="flex flex-col gap-2">
                      {[
                        "Thank you for reaching out! We'll get back to you shortly.",
                        "We've received your inquiry and are looking into it.",
                        "For faster support, please call us or visit the shop.",
                      ].map((q, i) => (
                        <button key={i} onClick={() => setTicketReply(q)}
                          className="px-3 py-2 bg-white border border-[#a6cfd5] text-[#46735c] text-[11px] font-bold rounded-xl hover:bg-[#d5e5db] transition-colors text-left">
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t border-[#e1ebf0] bg-white">
                  <div className="flex gap-2">
                    <input type="text" value={ticketReply} onChange={(e) => setTicketReply(e.target.value)}
                      placeholder={`Reply to ${activeTicket.name}...`}
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#6992a5] transition-colors" />
                    <a href={`mailto:${activeTicket.email}?subject=Re: Your Inquiry at Furpect Tails&body=${encodeURIComponent(ticketReply)}`}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${ticketReply.trim() ? "bg-[#6992a5] hover:bg-[#5b7b88] text-white" : "bg-gray-200 text-gray-400 pointer-events-none"}`}>
                      <SendIcon /> Reply via Email
                    </a>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5 font-medium">?? Opens your email client to send the reply.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportInbox;
